// frontend/src/pages/GettingStartedPage.jsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, UserPlus, Settings, FileText, CheckCircle2, ChevronRight } from "lucide-react";

export default function GettingStartedPage() {
  const steps = [
    {
      id: "01",
      icon: <Settings size={28} />,
      title: "Configure o perfil da sua clínica",
      description: "Antes de começar a atender, é importante deixar o sistema com a sua cara. Acesse a área de Configurações para adicionar sua logomarca, dados da clínica (CNPJ, endereço, telefone) e informações profissionais (CRO). Esses dados serão usados automaticamente em seus documentos, receitas e termos.",
      tip: "Ter a logo preenchida é essencial para a geração de documentos com aspecto profissional."
    },
    {
      id: "02",
      icon: <UserPlus size={28} />,
      title: "Cadastre seus primeiros pacientes",
      description: "Acesse a aba 'Pacientes' para adicionar seus primeiros contatos. O OdontoAssist permite centralizar o histórico clínico, evoluções e prontuários. Se você já tiver uma base de pacientes em outro sistema, entre em contato através do nosso WhatsApp para ajudarmos na importação.",
      tip: "Ao cadastrar um paciente, preencha o máximo de informações possível, isso ajuda nossa Inteligência Artificial a fornecer resumos mais precisos."
    },
    {
      id: "03",
      icon: <FileText size={28} />,
      title: "Personalize seus modelos de documento",
      description: "Você não precisa digitar contratos e termos de consentimento do zero para cada novo tratamento. Crie seus 'Modelos de Documentos' na aba de configurações. Use variáveis como {{nome_paciente}} para que o sistema preencha tudo automaticamente quando for gerar um PDF.",
      tip: "Já deixamos alguns modelos padrões pré-configurados que você pode usar e adaptar."
    },
    {
      id: "04",
      icon: <CheckCircle2 size={28} />,
      title: "Faça o primeiro atendimento com IA",
      description: "Com pacientes e modelos configurados, você está pronto. No Prontuário do paciente, experimente usar a 'Anamnese Inteligente' ou nossa IA Clínica assistente para resumir o caso ou apoiar seu diagnóstico com base na evolução preenchida no Odontograma.",
      tip: "Toda interação com a IA é sigilosa e os dados pessoais do paciente são protegidos com a mais estrita conformidade à LGPD."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans">
      <header className="border-b bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-14" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda" className="text-gray-500 hover:text-indigo-600 transition-colors font-semibold flex items-center gap-2">
              <ArrowLeft size={16} /> Central de Ajuda
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-4">
            <Link to="/ajuda" className="hover:underline">Ajuda</Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span>Tutoriais</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Primeiros Passos</h1>
          <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
            Bem-vindo ao OdontoAssist AI! Preparamos este guia rápido para você configurar sua clínica e começar a usar o sistema como um especialista em poucos minutos.
          </p>
        </div>

        <div className="space-y-8 relative">
          <div className="absolute top-8 left-8 bottom-8 w-1 bg-indigo-50 rounded-full hidden md:block" />
          
          {steps.map((step, idx) => (
            <div key={idx} className="relative z-10 flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="shrink-0 pt-2 flex items-start gap-6">
                <div className="w-16 h-16 bg-white border-4 border-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-sm font-black text-xl">
                  {step.id}
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                </div>
                
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  {step.description}
                </p>

                <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50 flex gap-3 items-start">
                  <div className="mt-0.5 text-indigo-600"><CheckCircle2 size={20} /></div>
                  <p className="text-indigo-900 text-sm font-medium leading-relaxed">
                    <strong className="text-indigo-700">Dica:</strong> {step.tip}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-indigo-900 text-white rounded-[2rem] p-10 lg:p-14 text-center border-4 border-indigo-50 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 blur-[80px] rounded-full" />
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-4">Tudo pronto!</h2>
            <p className="text-indigo-200 text-lg mb-8 max-w-xl mx-auto">
              Agora você tem uma clínica inteligente nas mãos. Se precisar de ajuda em alguma destas etapas, nossa equipe de suporte está online.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/login?trial=1" className="px-8 py-4 bg-white text-indigo-900 font-bold rounded-xl hover:bg-gray-50 transition shadow-lg shadow-black/10">
                Acessar meu painel
              </Link>
              <Link to="/contato" className="px-8 py-4 bg-indigo-800 text-white font-bold rounded-xl hover:bg-indigo-700 transition border border-indigo-700">
                Falar com Suporte
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
